import { useEffect, useMemo, useState } from 'react';
import { DEFAULT_LIMIT, VALUE_CHAIN_STATUS_DEFAULT } from 'constants/general';
import { useGetValueChainMapListQuery } from 'store/slices/valueChainMap/apis/valueChainMapApi';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type IValueChainMap from 'store/slices/valueChainMap/interfaces/IValueChainMap';

export const LAUNCHPAD_HUB_ACTIVE_VCM_KEY = 'attaima-launchpad-hub-active-vcm';

export default function useLaunchpadActiveValueChainMap(): {
  options: IOption[],
  maps: IValueChainMap[],
  selectedMapId: string | undefined,
  selectedMap: IValueChainMap | undefined,
  setSelectedMapId: (mapId: string | undefined) => void,
  isLoading: boolean,
  isEmpty: boolean,
  hasActiveVcm: boolean,
} {
  const [selectedMapId, setSelectedMapId] = useState<string | undefined>();

  const {
    data,
    isFetching,
  } = useGetValueChainMapListQuery({
    limit: DEFAULT_LIMIT,
    offset: 0,
    status: VALUE_CHAIN_STATUS_DEFAULT,
  });

  const maps = useMemo(() => data?.data?.items ?? [], [data?.data?.items]);

  const options: IOption[] = useMemo(
    () => maps.map((map) => ({
      label: map.name,
      value: String(map.id),
    })),
    [maps],
  );

  // Default to the first value chain map in the list.
  useEffect(() => {
    setSelectedMapId(maps.length ? String(maps[0].id) : undefined);
  }, [maps]);

  const selectedMap = useMemo(
    () => maps.find((map) => String(map.id) === selectedMapId),
    [maps, selectedMapId],
  );

  const isEmpty = !isFetching && maps.length === 0;
  const hasActiveVcm = maps.length > 0 && Boolean(selectedMapId);

  return {
    options,
    maps,
    selectedMapId,
    selectedMap,
    setSelectedMapId,
    isLoading: isFetching,
    isEmpty,
    hasActiveVcm,
  };
}
